// Reconocimiento de canción por micrófono (Tarea 19D, Req. 12.1, 12.3–12.6).
//
// Solicita permiso de micrófono, graba un clip corto y lo envía a la API como PCM
// base64 (lo que Shazam exige: 44100 Hz, mono, 16-bit LE). La conversión a PCM
// (ver `audioPcm.ts`) tiene dos vías, una fallback de la otra: captura PCM EN VIVO
// desde el micrófono (principal, fiable en Safari/iOS) y, si no sirve, decodifica
// el blob del MediaRecorder. Muestra el resultado: track/artista/álbum, la
// candidata de vinilo y si está en catálogo/wishlist. Si no reconoce, ofrece la
// búsqueda manual (fallback — Req. 12.8).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Link } from 'react-router-dom';
import { useApi } from '../api/ApiContext.js';
import { AlertMessage, Badge, Card, Icon, PageHeader } from '../ui/components.js';
import type { AudioResult, AudioRecognitionReason } from '../types.js';
import { useAudioClip } from './useAudioClip.js';

/** Duración máxima de la grabación (ms). Se graba más de lo que acepta Shazam
 *  (~5.6s): de la grabación larga se envía la ventana con más energía. */
const MAX_MS = 15000;

export function AudioView() {
  const { t } = useTranslation();
  const api = useApi();
  const clip = useAudioClip(MAX_MS);
  const [result, setResult] = useState<AudioResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Estado de la vista: 'done' cuando ya hay resultado; si no, refleja el clip.
  const [done, setDone] = useState(false);
  const state: 'idle' | 'recording' | 'processing' | 'done' = done ? 'done' : clip.state;

  async function startRecording() {
    setError(null);
    setResult(null);
    setDone(false);
    const base64 = await clip.start();
    // `start()` resuelve cuando termina la grabación (por tiempo o por `stop()`).
    if (!base64) {
      // Sin audio utilizable: si el hook reportó un error de micrófono, mostrarlo.
      if (clip.error) setError(clip.error);
      else setError(t('audio.errPrepare'));
      return;
    }
    try {
      const res = await api.audio.recognize({ audio_base64: base64 });
      setResult(res);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('audio.errRecognize'));
    }
  }

  function stopRecording() {
    clip.stop();
  }

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('audio.pageTitle')}
        subtitle={t('audio.pageSubtitle')}
        back
      />
      <div className="px-margin-mobile py-space-md flex flex-col gap-space-md">
        <AlertMessage message={error} />

        {/* Radar acústico */}
        <div className="surface-card flex flex-col items-center gap-space-md py-space-lg">
          <span className="section-label text-on-surface-variant">{t('audio.snapshot')}</span>

          {/* Botón radar con anillos */}
          <button
            type="button"
            onClick={() => (state === 'recording' ? stopRecording() : state === 'idle' ? void startRecording() : undefined)}
            disabled={state === 'processing'}
            className="relative w-40 h-40 rounded-full flex items-center justify-center focus:outline-none"
          >
            <span
              className={`absolute inset-0 rounded-full border ${
                state === 'recording' ? 'border-priority-urgent animate-ping' : 'border-border-subtle'
              }`}
            />
            <span
              className={`absolute inset-4 rounded-full border ${
                state === 'recording' ? 'border-primary-container/60 animate-pulse' : 'border-border-subtle'
              }`}
            />
            <span
              className={`w-24 h-24 rounded-full flex items-center justify-center shadow-[0_0_24px_rgba(56,189,248,0.35)] ${
                state === 'recording' ? 'bg-priority-urgent text-white' : 'bg-primary-container text-on-primary'
              }`}
            >
              <Icon name={state === 'recording' ? 'stop' : state === 'processing' ? 'graphic_eq' : 'mic'} className="text-[40px]" />
            </span>
          </button>

          <p className="font-body-sm text-body-sm text-on-surface-variant text-center max-w-xs">
            {state === 'idle' && t('audio.idleHint')}
            {state === 'recording' && t('audio.recordingHint')}
            {state === 'processing' && t('audio.processingHint')}
            {state === 'done' && t('audio.doneHint')}
          </p>

          {/* Barras de frecuencia (decorativas) */}
          {state === 'recording' && (
            <div className="flex items-end gap-1 h-8">
              {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                <span
                  key={i}
                  className="w-1.5 bg-primary-container rounded-full animate-pulse"
                  style={{ height: `${20 + ((i * 37) % 80)}%`, animationDelay: `${i * 90}ms` }}
                />
              ))}
            </div>
          )}
        </div>

        {state === 'done' && result && (
          <Card title={t('audio.resultTitle')} icon="graphic_eq">
            {result.matched ? (
              <>
                <div className="flex items-center gap-space-xs mb-space-sm">
                  {result.in_wishlist && <Badge tone="warn">{t('audio.inWishlist')}</Badge>}
                  {result.in_catalog && <Badge tone="success">{t('audio.inCatalog')}</Badge>}
                </div>
                <p className="font-body-md text-body-md text-on-surface">
                  <span className="font-bold">{result.recognized_track ?? '—'}</span>
                  {result.recognized_artist ? ` · ${result.recognized_artist}` : ''}
                  {result.recognized_album ? ` · ${result.recognized_album}` : ''}
                </p>
                {result.identified && (
                  <p className="font-body-sm text-body-sm text-on-surface-variant mt-space-xs">
                    {t('audio.relatedVinyl', {
                      artist: result.identified.artist ?? '—',
                      title: result.identified.title ?? '—',
                    })}
                    {result.identified.year ? ` (${result.identified.year})` : ''}
                  </p>
                )}
                <button className="btn-secondary w-full mt-space-md" type="button" onClick={() => { setDone(false); setResult(null); }}>
                  <Icon name="replay" className="text-[18px]" /> {t('audio.recognizeAnother')}
                </button>
              </>
            ) : (
              <>
                <div className="p-space-sm rounded-lg bg-goldmine-vg/15 border border-goldmine-vg/40 text-goldmine-vg font-body-sm text-body-sm">
                  {notMatchedMessage(t, result.reason)}
                </div>
                <p className="font-body-sm text-body-sm text-on-surface-variant mt-space-sm">
                  {t('audio.tryManualPre')}
                  <Link to="/quick-lookup" className="text-primary underline">{t('audio.tryManualLink')}</Link>{t('audio.tryManualPost')}
                </p>
                <button className="btn-secondary w-full mt-space-md" type="button" onClick={() => { setDone(false); setResult(null); }}>
                  <Icon name="replay" className="text-[18px]" /> {t('audio.retry')}
                </button>
              </>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}

/** Mensaje para el usuario según el motivo de que no haya coincidencia. */
function notMatchedMessage(t: TFunction, reason: AudioRecognitionReason): string {
  switch (reason) {
    case 'quota_exceeded':
      return t('audio.reasonQuota');
    case 'provider_error':
      return t('audio.reasonProvider');
    case 'no_match':
    default:
      return t('audio.reasonNoMatch');
  }
}

